import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { LocationDto } from '@travel-planner/library-travel-planner-api';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { LocationsActions } from './locations.actions';
import { LoadingState } from '../../../../core/models/common';

export interface LocationsState extends EntityState<LocationDto> {
    loadingState: LoadingState;
    total: number | null;
    loadingEntityIds: (number | string)[];
}

export const locationsAdapter: EntityAdapter<LocationDto> = createEntityAdapter<LocationDto>();

const initialState: LocationsState = locationsAdapter.getInitialState({
    loadingState: 'Initial',
    total: 0,
    loadingEntityIds: [],
});

export const locationsFeature = createFeature({
    name: 'locations',
    reducer: createReducer(
        initialState,
        on(LocationsActions.loadLocations, (state) => ({
            ...state,
            loadingState: 'Loading' as const,
        })),
        on(LocationsActions.loadLocationsSuccess, (state, { locations, total }) => {
            if (total != null) {
                return {
                    ...locationsAdapter.setAll(locations, state),
                    total: total,
                    loadingState: 'Loaded' as const,
                };
            }
            return {
                ...locationsAdapter.addMany(locations, state),
                loadingState: 'Loaded' as const,
            };
        }),
        on(LocationsActions.loadLocationSuccess, (state, { location }) => {
            return locationsAdapter.setOne(location, state);
        }),
        on(LocationsActions.approveAiGeneratedContent, (state, { id }) => {
            const loadingEntityIds = [...state.loadingEntityIds, id];
            return { ...state, loadingEntityIds };
        }),
        on(LocationsActions.approveAiGeneratedContentSuccess, (state, { location }) => {
            const loadingEntityIds = state.loadingEntityIds.filter(id => id !== location.id);
            return {
                ...locationsAdapter.setOne(location, state),
                loadingEntityIds
            };
        }),
        on(LocationsActions.addLocation, (state) => ({
            ...state,
            loadingEntityIds: [...state.loadingEntityIds, 'new-location']
        })),
        on(LocationsActions.updateLocation, (state, { id }) => {
            const loadingEntityIds = [...state.loadingEntityIds, id];
            return { ...state, loadingEntityIds };
        }),
        on(
            LocationsActions.updateLocationSuccess,
            LocationsActions.uploadLocationImagesSuccess,
            LocationsActions.reorderLocationImagesSuccess,
            (state, { updatedLocation }) => {
                const loadingEntityIds = state.loadingEntityIds.filter(id => id !== (updatedLocation.id ?? 'new-location'));
                return {
                    ...locationsAdapter.setOne(updatedLocation, state),
                    loadingEntityIds
                };
            }
        ),
        on(LocationsActions.deleteLocationImageSuccess, (state, { locationId, imageId }) => {
            const loc = state.entities[locationId];
            if (!loc) return state;
            const updatedLoc = {
                ...loc,
                images: (loc.images || []).filter(img => img.id !== imageId)
            };
            const loadingEntityIds = state.loadingEntityIds.filter(id => id !== locationId);
            return {
                ...locationsAdapter.setOne(updatedLoc, state),
                loadingEntityIds
            };
        }),
        on(LocationsActions.addLocationSuccess, (state, { location }) => {
            const loadingEntityIds = state.loadingEntityIds.filter(id => id !== 'new-location');
            return {
                ...locationsAdapter.setOne(location, state),
                loadingEntityIds
            };
        }),
        on(LocationsActions.deleteLocationSuccess, (state, { id }) => {
            return locationsAdapter.removeOne(id, state);
        }),
        on(
            LocationsActions.loadLocationsFailure,
            LocationsActions.loadLocationFailure,
            LocationsActions.deleteLocationFailure,
            (state) => ({
                ...state,
                loadingState: 'Error' as const,
            })
        ),
        on(LocationsActions.addLocationFailure, (state) => ({
            ...state,
            loadingEntityIds: state.loadingEntityIds.filter(id => id !== 'new-location'),
            loadingState: 'Error' as const,
        })),
        on(
            LocationsActions.updateLocationFailure,
            LocationsActions.approveAiGeneratedContentFailure,
            LocationsActions.uploadLocationImagesFailure,
            LocationsActions.reorderLocationImagesFailure,
            (state, { id }) => ({
                ...state,
                loadingEntityIds: state.loadingEntityIds.filter(itemId => itemId !== id),
                loadingState: 'Error' as const,
            })
        ),
        on(LocationsActions.deleteLocationImageFailure, (state, { id }) => ({
            ...state,
            loadingEntityIds: state.loadingEntityIds.filter(itemId => itemId !== id),
            loadingState: 'Error' as const,
        })),
    )
});

const { selectLocationsState } = locationsFeature;

const selectSafeLocationsState = createSelector(
    selectLocationsState,
    (state) => state ?? initialState
);

const { selectAll, selectEntities, selectTotal } =
    locationsAdapter.getSelectors(selectSafeLocationsState);

export const selectLocations = selectAll;

export const selectLocationsEntities = selectEntities;

export const selectLocationsLoadedCount = selectTotal;

export const selectLocationsTotal = createSelector(
    selectSafeLocationsState,
    (state: LocationsState) => state.total
);

export const selectLocation = (id: string) =>
    createSelector(selectSafeLocationsState, (state) => state.entities[id] || null);
